import '../historic-metrics.scss'

import {
    Box,
    Button,
    Checkbox,
    Dropdown,
    Form,
    Input,
    ReactDatePicker,
} from '@missionlabs/smartagent-app-components'
import AgentDropdown from 'components/Dropdowns/agents'
import QueueDropdown from 'components/Dropdowns/queues'
import useApp from 'hooks/redux/useApp'
import useContact from 'hooks/redux/useContact'
import useHasFeature, { AppFeatures, AppSubFeatures, useCompanyHasFeature } from 'hooks/useHasFeature'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { H, Level } from 'react-accessible-headings'
import { batch, useDispatch, useSelector } from 'react-redux'
import { ClipLoader } from 'react-spinners'
import {
    fetchInteractions,
    resetFormParams,
    setAgent,
    setAppContactId,
    setBargedOnly,
    setLiveVideoAssist,
    setCallDurationEndTime,
    setCallDurationStartTime,
    setChannel,
    setChatEmail,
    setChatOrderNumber,
    setContactID,
    setCustomerPhone,
    setEmail,
    setEmailSearchType,
    setEndDate,
    setEndTime,
    setExternalID,
    setHoldDurationEndTime,
    setHoldDurationStartTime,
    setInitiationMethod,
    setKeyword,
    setQueue,
    setSocialHandle,
    setStartDate,
    setStartTime,
    setSystemPhoneNumber,
    setUniqueSocialID,
    setAccountId,
} from 'store/contactSearch/contactSearch.actions'
import {
    ContactChannel,
    EmailContactType,
    IContactSearchOptions,
    togglePlaceholderTypes,
} from 'store/contactSearch/contactSearch.state'
import {
    getContactSearchQueryString,
    optionsFactory,
} from 'store/contactSearch/contactSearch.utils'
import RootState from 'store/state'
import { isValidEmailAddress } from 'utils'

export interface Props {
    searching: boolean
}

export enum ChannelType {
    NONE='NONE',
    VOICE='VOICE',
    CHAT='CHAT',
    SOCIAL='SOCIAL'
}

// Time and Date do not have placeholders in HTML5. So
// this is a workaround to implement a placeholder functionality in them.
// The type switches between text and time/date for it to work.
const initialPlaceholder: togglePlaceholderTypes = { startDate: 'text' }
initialPlaceholder.callDurationFrom,
    initialPlaceholder.callDurationTo,
    initialPlaceholder.endDate,
    initialPlaceholder.startDate,
    initialPlaceholder.startTime,
    initialPlaceholder.endTime,
    initialPlaceholder.holdDurationFrom,
    (initialPlaceholder.holdDurationTo = 'text')

const ContactSearch: React.FC<Props> = ({ searching }) => {
    const appContact = useContact()
    const dispatch = useDispatch()
    const hasFeature = useHasFeature()
    const companyHasFeature = useCompanyHasFeature()

    const contactIDInput: React.RefObject<HTMLInputElement | null> | null | undefined = useRef(null)

    const hasAdvancedSearchParams = () => {
        return !!(
            contactID ||
            externalID ||
            phoneNumber ||
            callDurationStartTime ||
            callDurationEndTime ||
            holdDurationEndTime ||
            holdDurationStartTime ||
            phoneNumber ||
            initiationMethod ||
            bargedOnly
        )
    }

    const socialsSelected = () => {
        switch (channel.label) {
            case 'Instagram Comments':
            case 'Facebook Comments':
            case 'Twitter Tweets':
            case 'Instagram DM':
            case 'Twitter DM':
            case 'Facebook DM':
            case 'WhatsApp DM':
            case 'SMS DM':
                return true
            default:
                return false
        }
    }

    const appContactId = useSelector((state: RootState) => state.contactSearch?.appContactId)

    const formParams = useSelector((state: RootState) => state.contactSearch.formParams)
    let {
        contactID,
        initiationMethod,
        phoneNumber,
        externalID,
        callDurationStartTime,
        callDurationEndTime,
        holdDurationStartTime,
        holdDurationEndTime,
        bargedOnly,
    } = formParams
    const {
        agent,
        queue,
        startDate,
        startTime,
        endDate,
        endTime,
        channel,
        socialHandle,
        systemPhoneNumber,
        uniqueSocialId,
        email,
        emailSearchType,
        keyword,
        liveVideoAssist,
        accountId,
    } = formParams

    const [typeDropdownValue, setTypeDropdownValue] = useState<string | undefined>(undefined)
    const [searchValue, setSearchValue] = useState<string>('')
    const [searchLabel, setSearchLabel] = useState<string>('')
    const [searchPlaceholder, setSearchPlaceholder] = useState<string>('')
    const [selectedChannelType, setSelectedChannelType] = useState<ChannelType>(ChannelType.NONE)
    const [togglePlaceholder, setTogglePlaceholder] =
        useState<togglePlaceholderTypes>(initialPlaceholder)
    const [showAdvanced, setShowAdvanced] = useState<boolean>(hasAdvancedSearchParams())
    const [isEmailValid, setIsEmailValid] = useState<boolean>(
        channel.data === ContactChannel.EMAIL && isValidEmailAddress(email),
    )
    const [options, setOptions] = useState<IContactSearchOptions>({
        initiation: optionsFactory.initiation(),
        channel: optionsFactory.channel(),
        socialSearchType: optionsFactory.socialSearchType(),
        chatSearchType: optionsFactory.chatSearchType(),
        emailSearchType: optionsFactory.emailSearchType(),
    })

    const isWhatsAppChannelSelected = channel.label.includes('WhatsApp')
    const isSmsChannelSelected = channel.label.includes('SMS')
    const isWhatsAppOrSmsChannelSelected = isWhatsAppChannelSelected || isSmsChannelSelected

    const app = useApp()
    const hasRestrictQueueAccessFeature = !!app.features.find(
        (feature) => feature.ID === AppFeatures.RESTRICT_QUEUE_ACCESS,
    )

    const hasContactSearchByAccountIdFeature = hasFeature(AppFeatures.CONTACT_SEARCH_ACCOUNT_ID)

    const invalidStartDate = () => {
        if (startDate > endDate) {
            return 'Start date must be before the end date.'
        }
        return ''
    }

    const invalidEndDate = () => {
        if (startDate > endDate) {
            return 'Start date must be before the end date.'
        }
        return ''
    }
    useEffect(() => {
        invalidStartTime()
    }, [startDate, endDate, startTime, endTime])

    const [validationMessage, setValidationMessage] = useState('')

    const invalidStartTime = () => {
        let message = ''
        if (startDate === endDate && startTime >= endTime) {
            message = 'Start time must be before the end time on the same day.'
        }
        setValidationMessage(message)
        return message
    }

    const isSearchInvalid = useMemo(() => {
        const dateError = () => {
            return startDate > endDate || (startDate === endDate && startTime >= endTime)
        }

        return (
            searching ||
            !getContactSearchQueryString(formParams, hasRestrictQueueAccessFeature) ||
            !!(typeDropdownValue && !searchValue && !isWhatsAppOrSmsChannelSelected) ||
            (channel.data === ContactChannel.EMAIL &&
                email.length > 0 &&
                !isValidEmailAddress(email)) ||
            dateError()
        )
    }, [formParams, typeDropdownValue, searchValue, searching])

    useEffect(() => {
        if (isSearchInvalid) return

        setTimeout(() => {
            contactIDInput?.current?.focus()
        }, 300)

        const getSocialChannelDropdownOption = (channel: string) => {
            return {
                label: channel,
                data: channel,
            }
        }

        setOptions((prevState) => {
            const newState = { ...prevState, channel: [...prevState.channel] }
            newState.channel.push(getSocialChannelDropdownOption('Facebook DM'))
            newState.channel.push(getSocialChannelDropdownOption('Instagram DM'))
            newState.channel.push(getSocialChannelDropdownOption('Facebook Comments'))
            newState.channel.push(getSocialChannelDropdownOption('Instagram Comments'))
            return newState
        })

        if (companyHasFeature(AppFeatures.WHATSAPP)) {
            setOptions((prevState) => ({
                ...prevState,
                channel: [...prevState.channel, getSocialChannelDropdownOption('WhatsApp DM')],
            }))
        }
        if (companyHasFeature(AppFeatures.SMS_MESSAGING)) {
            setOptions((prevState) => ({
                ...prevState,
                channel: [...prevState.channel, getSocialChannelDropdownOption('SMS DM')],
            }))
        }

        if (socialsSelected() && uniqueSocialId) {
            setTypeDropdownValue('Unique Social ID')
            setSearchLabel('Unique Social ID')
            setSearchValue(uniqueSocialId)
        } else if (socialsSelected() && socialHandle) {
            setTypeDropdownValue(isWhatsAppOrSmsChannelSelected ? 'Phone Number' : 'Social Handle')
            setSearchLabel(isWhatsAppOrSmsChannelSelected ? 'Phone Number' : 'Social Handle')
            setSearchValue(socialHandle)
        } else {
            setTypeDropdownValue(undefined)
            setSearchValue('')
        }

    }, [])

    useEffect(() => {
        switch (typeDropdownValue) {
            case 'Social Handle':
                dispatch(setSocialHandle(searchValue))
                break
            case 'Unique Social ID':
                dispatch(setUniqueSocialID(searchValue))
                break
            case 'Email':
                dispatch(setChatEmail(searchValue))
                break
            case 'Order Number':
                dispatch(setChatOrderNumber(searchValue))
                break
            default:
                resetSocialChatDropdownDispatch()
        }
        if (isWhatsAppOrSmsChannelSelected) dispatch(setSocialHandle(searchValue))
    }, [searchValue, searchLabel])

    useEffect(() => {
        if (channel.data === ContactChannel.EMAIL) {
            // switching into email channel - set default search type of All
            dispatch(
                setEmailSearchType({
                    data: EmailContactType['All'],
                    label: EmailContactType['All'],
                }),
            )
        } else {
            // switching away from email channel - clear email-related form parameters
            batch(() => {
                email && channel.data !== ContactChannel.WEBFORM && dispatch(setEmail(''))
                ;(emailSearchType.data || emailSearchType.label) &&
                    dispatch(setEmailSearchType({ data: '', label: '' }))
                keyword && dispatch(setKeyword(''))
            })
        }
    }, [channel])

    useEffect(() => {
        autoPopulateUniqueSocialIdOnWidgetLoad()
    }, [appContact?.ID])

    const autoPopulateUniqueSocialIdOnWidgetLoad = () => {
        if (!appContact) return
        const { attributes, ID } = appContact
        if (
            !attributes ||
            !attributes['sa-social-media-platform'] ||
            !attributes['sa-customer-endpoint-id'] ||
            appContactId === ID
        )
            return

        const uniqueSocialId = attributes['sa-customer-endpoint-id']
        setSocialChannelFromAttributes(attributes['sa-sub-channel'])
        setTypeDropdownValue('Unique Social ID')
        setSearchValue(uniqueSocialId)
        setSearchLabel('Unique Social ID')

        batch(() => {
            dispatch(fetchInteractions(`uniqueSocialId=${uniqueSocialId}`))
            dispatch(setAppContactId(ID))
        })
    }

    const setSocialChannelFromAttributes = (channel: string) => {
        switch (channel) {
            case 'MESSENGER':
                dispatchChannelChange('Facebook DM')
                break
            case 'DM':
                dispatchChannelChange('Twitter DM')
                break
            case 'IG-DM':
                dispatchChannelChange('Instagram DM')
                break
            case 'POST':
                dispatchChannelChange('Facebook Comments')
                break
            case 'IG-COMMENT':
                dispatchChannelChange('Instagram Comments')
                break
            case 'TWEET':
                dispatchChannelChange('Twitter Tweets')
                break
            case 'WHATSAPP-DM':
                dispatchChannelChange('WhatsApp DM')
                break
            case 'SMS-DM':
                dispatchChannelChange('SMS DM')
                break
        }
    }

    const dispatchChannelChange = (channel: string) =>
        dispatch(setChannel({ label: channel, data: channel }))

    const resetSocialChatDropdownDispatch = () => {
        batch(() => {
            dispatch(setUniqueSocialID(''))
            dispatch(setSocialHandle(''))
            dispatch(setChatEmail(''))
            dispatch(setChatOrderNumber(''))
        })
    }

    useEffect(() => {
        if (isWhatsAppOrSmsChannelSelected) {
            setSearchLabel('Phone Number')
            setTypeDropdownValue('Phone Number')
        }
    }, [isWhatsAppChannelSelected, isSmsChannelSelected])

    // Update selected channel type when channel changes
    useEffect(() => {
        switch (channel.label) {
            case 'WhatsApp DM':
            case 'SMS DM':
            case 'Twitter DM':
            case 'Facebook DM':
            case 'Instagram DM':
            case 'Twitter Tweets':
            case 'Instagram Comments':
            case 'Facebook Comments':
                setSelectedChannelType(ChannelType.SOCIAL)
                break
            case 'Chat':
                setSelectedChannelType(ChannelType.CHAT)
                break
            case 'Voice':
                setSelectedChannelType(ChannelType.VOICE)
                break
            default:
                setSelectedChannelType(ChannelType.NONE)
        }
    }, [channel])

    const onReset = () => dispatch(resetFormParams())

    const onSubmit = () =>
        dispatch(
            fetchInteractions(
                getContactSearchQueryString(formParams, hasRestrictQueueAccessFeature),
            ),
        )

    if (!showAdvanced) {
        contactID = ''
        externalID = ''
        phoneNumber = ''
        callDurationStartTime = ''
        callDurationEndTime = ''
        holdDurationStartTime = ''
        holdDurationEndTime = ''
        bargedOnly = false
        initiationMethod = undefined
    }

    const startDateObj = useMemo(() => (startDate ? new Date(startDate) : new Date()), [startDate])
    const endDateObj = useMemo(() => (endDate ? new Date(endDate) : new Date()), [endDate])

    //used to reset the date and times on use of clear button
    const [uniqueKey, setUniqueKey] = useState(Date.now())

    const handleClearStartDate = () => {
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        dispatch(setStartDate(yesterday))
        setUniqueKey(Date.now())
    }

    const handleClearEndDate = () => {
        const today = new Date()
        dispatch(setEndDate(today))
        setUniqueKey(Date.now())
    }

    const handleClearTime = () => {
        dispatch(setStartTime(startTime))
        dispatch(setEndTime(endTime))
        setUniqueKey(Date.now())
    }

    const handleClearBothDates = () => {
        handleClearStartDate()
        handleClearEndDate()
        handleClearTime()
    }

    const EmailInput = (
        <Input
            title="Email address"
            label="Email address"
            value={email}
            onChange={(payload) => dispatch(setEmail(payload))}
            onValidate={(value) => {
                const isValid = value.length > 0 ? isValidEmailAddress(value) : true
                setIsEmailValid(isValid)
                return !isValid ? 'Please enter a valid email address.' : ''
            }}
            showError
        />
    )
    return (
        <Box
            className="contact-search-box"
            alt
            boxLabel="Contact Search"
            header={<H>Contact Search</H>}
        >
            <Level>
                <Form className="contact-search-form" onReset={onReset} onSubmit={onSubmit}>
                    <div className="row column">
                        <div className="row">
                            <ReactDatePicker
                                title="Select start date"
                                key={uniqueKey}
                                label="Start Date"
                                value={startDateObj}
                                handleChange={(date) => dispatch(setStartDate(date))}
                                onValidate={invalidStartDate}
                                showError
                            />
                            <div>
                                <Input
                                    title="Select start time"
                                    label="Start Time"
                                    value={startTime}
                                    key={uniqueKey + 2}
                                    type={togglePlaceholder.startTime}
                                    style={{
                                        width: validationMessage ? '100%' : '96px',
                                        border: validationMessage
                                            ? '2px solid red'
                                            : '1px solid rgb(86, 86, 86)',
                                    }}
                                    onChange={(startTime) => dispatch(setStartTime(startTime))}
                                    onValidate={invalidStartTime}
                                    placeholder="00:00"
                                    onFocus={() => {
                                        setTogglePlaceholder((obj) => ({
                                            ...obj,
                                            startTime: 'time',
                                        }))
                                    }}
                                    onBlur={() => {
                                        setTogglePlaceholder((obj) => ({
                                            ...obj,
                                            startTime: 'text',
                                        }))
                                    }}
                                />
                                {validationMessage && (
                                    <div className="input-error">{validationMessage}</div>
                                )}
                            </div>

                            <ReactDatePicker
                                key={uniqueKey + 1}
                                title="Select end date"
                                label="End Date"
                                value={endDateObj}
                                handleChange={(date) => dispatch(setEndDate(date))}
                                onValidate={invalidEndDate}
                                showError
                            />

                            <Input
                                title="Select end time"
                                label="End Time"
                                type={togglePlaceholder.endTime}
                                key={uniqueKey + 3}
                                value={endTime}
                                onChange={(endTime) => dispatch(setEndTime(endTime))}
                                placeholder="00:00"
                                style={{
                                    width: '96px',
                                }}
                                onFocus={() => {
                                    setTogglePlaceholder((obj) => ({ ...obj, endTime: 'time' }))
                                }}
                                onBlur={() => {
                                    setTogglePlaceholder((obj) => ({ ...obj, endTime: 'text' }))
                                }}
                            />

                            <QueueDropdown
                                className="input"
                                blankOption
                                filter
                                selectedQueueID={queue?.ID}
                                onSelect={(queue) => dispatch(setQueue(queue))}
                            />

                            <AgentDropdown
                                className="input"
                                blankOption
                                filter
                                selectedAgentID={agent?.ID}
                                onSelect={(agent) => dispatch(setAgent(agent))}
                            />
                        </div>
                        <div className="row bottom">
                            <Dropdown
                                className="input"
                                title="Select channel"
                                filter
                                label="Channel"
                                value={channel.label}
                                options={options.channel}
                                onChange={(option) => {
                                    setSearchValue('')
                                    setTypeDropdownValue(undefined)
                                    return dispatch(setChannel(option))
                                }}
                            />
                            {((selectedChannelType === ChannelType.VOICE &&
                                hasFeature(AppFeatures.VIDEO_MEETING, [AppSubFeatures.VIDEO_MEETING_VIEW_RECORDS])
                            ) && (
                                <Checkbox
                                    square
                                    checked={liveVideoAssist}
                                    label="Live Video Assist"
                                    onChange={() => dispatch(setLiveVideoAssist(!liveVideoAssist))}
                                    ariaLabel="Live Video Assist"
                                />
                            ))}
                            {(selectedChannelType === ChannelType.SOCIAL ||
                                (selectedChannelType === ChannelType.CHAT &&
                                    hasFeature(AppFeatures.CHAT_CONTACT_SEARCH_OPTIONS))) &&
                                (isWhatsAppOrSmsChannelSelected ? (
                                    <>
                                        <Input
                                            title="Enter customer phone number"
                                            label="Customer Phone Number"
                                            placeholder="E.g. +447798765432"
                                            value={searchValue}
                                            onChange={setSearchValue}
                                        />
                                        <Input
                                            title="Enter system phone number"
                                            label="System Phone Number"
                                            placeholder="E.g. +447798765432"
                                            value={systemPhoneNumber}
                                            onChange={(systemPhone) => 
                                                dispatch(setSystemPhoneNumber(systemPhone))
                                            }
                                        />   
                                    </>
                                ) : (
                                    <>
                                        <Dropdown
                                            filter
                                            label="Search By"
                                            value={typeDropdownValue}
                                            options={
                                                selectedChannelType === ChannelType.CHAT
                                                    ? options.chatSearchType
                                                    : options.socialSearchType
                                            }
                                            onChange={(option) => {
                                                setSearchLabel(option.label)
                                                switch (option.label) {
                                                    case 'Social Handle':
                                                        setSearchPlaceholder('E.g. @smartagent')
                                                        break
                                                    case 'Phone Number':
                                                        setSearchPlaceholder('E.g. 447798765432')
                                                        break
                                                    case 'Unique Social ID':
                                                        setSearchPlaceholder('E.g. 123 456 7890')
                                                        break
                                                    case 'Email':
                                                        setSearchPlaceholder(
                                                            'E.g. user@smartagent.app',
                                                        )
                                                        break
                                                    case 'Order Number':
                                                        setSearchPlaceholder('E.g. 123456789abc')
                                                        break
                                                }
                                                resetSocialChatDropdownDispatch()
                                                setTypeDropdownValue(option?.label)
                                            }}
                                        />
                                        {!typeDropdownValue && (
                                            <div className="contact-search-flex-fill" />
                                        )}
                                        {typeDropdownValue && (
                                            <Input
                                                label={searchLabel}
                                                placeholder={searchPlaceholder}
                                                value={searchValue}
                                                onChange={setSearchValue}
                                            />
                                        )}
                                    </>
                                ))}
                            {channel.data === ContactChannel.WEBFORM && EmailInput && (
                                <>
                                    {EmailInput}
                                    <Input
                                        title="Keyword"
                                        label="Keyword"
                                        placeholder="Maximum of 30 characters"
                                        value={keyword ?? ''}
                                        onChange={(payload) => dispatch(setKeyword(payload))}
                                        showError
                                        maxLength={30}
                                    />
                                </>
                            )}
                            {channel.data === ContactChannel.EMAIL && (
                                <>
                                    <Dropdown
                                        title="Search by email type"
                                        filter
                                        label="Search By:"
                                        value={emailSearchType.label}
                                        options={options.emailSearchType}
                                        onChange={(option) => dispatch(setEmailSearchType(option))}
                                    />
                                    {EmailInput}
                                    <Input
                                        title="Keyword"
                                        label="Keyword"
                                        placeholder="Max 30 characters"
                                        value={keyword ?? ''}
                                        onChange={(payload) => dispatch(setKeyword(payload))}
                                        showError
                                        maxLength={30}
                                    />
                                </>
                            )}

                            {channel.data === ContactChannel.TASK &&
                                hasContactSearchByAccountIdFeature && (
                                    <Input
                                        title="Account ID"
                                        label="Account Number"
                                        value={accountId ?? ''}
                                        onChange={(value) => dispatch(setAccountId(value))}
                                    />
                                )}

                            {showAdvanced && (
                                <>
                                    <Input
                                        title="Enter contact ID"
                                        ref={contactIDInput}
                                        label="Contact ID"
                                        value={contactID}
                                        onChange={(contactID) => dispatch(setContactID(contactID))}
                                    />

                                    <Input
                                        title="Enter external ID"
                                        label="External ID"
                                        value={externalID}
                                        onChange={(externalID) =>
                                            dispatch(setExternalID(externalID))
                                        }
                                    />
                                    <Input
                                        title="Enter customer phone number"
                                        label="Customer phone number"
                                        value={phoneNumber}
                                        onChange={(customerPhoneNumber) =>
                                            dispatch(setCustomerPhone(customerPhoneNumber))
                                        }
                                    />
                                    <Dropdown
                                        filter
                                        title="Select initiation Method"
                                        label="Initiation Method"
                                        value={initiationMethod?.label}
                                        options={options.initiation}
                                        onChange={(option) => dispatch(setInitiationMethod(option))}
                                    />

                                    <div className="double-input">
                                        <Input
                                            title="Select minimal call duration"
                                            label="Call Duration"
                                            value={callDurationStartTime}
                                            onChange={(callDurationStartTime) =>
                                                dispatch(
                                                    setCallDurationStartTime(callDurationStartTime),
                                                )
                                            }
                                            type={togglePlaceholder.callDurationFrom}
                                            placeholder="From"
                                            aria-label="From: minimal call duration"
                                            step={30}
                                            onFocus={() => {
                                                setTogglePlaceholder((obj) => ({
                                                    ...obj,
                                                    callDurationFrom: 'time',
                                                }))
                                            }}
                                            style={{
                                                width: '124px',
                                            }}
                                            onBlur={() => {
                                                setTogglePlaceholder((obj) => ({
                                                    ...obj,
                                                    callDurationFrom: callDurationStartTime
                                                        ? 'time'
                                                        : 'text',
                                                }))
                                            }}
                                        />
                                        <Input
                                            title="Select maximum call duration"
                                            label="‏‏‎ ‎"
                                            value={callDurationEndTime}
                                            onChange={(callDurationEndTime) =>
                                                dispatch(
                                                    setCallDurationEndTime(callDurationEndTime),
                                                )
                                            }
                                            type={togglePlaceholder.callDurationTo}
                                            placeholder="To"
                                            aria-label="To: maximum call duration"
                                            step={30}
                                            onFocus={() => {
                                                setTogglePlaceholder((obj) => ({
                                                    ...obj,
                                                    callDurationTo: 'time',
                                                }))
                                            }}
                                            style={{
                                                width: '124px',
                                            }}
                                            onBlur={() => {
                                                setTogglePlaceholder((obj) => ({
                                                    ...obj,
                                                    callDurationTo: callDurationEndTime
                                                        ? 'time'
                                                        : 'text',
                                                }))
                                            }}
                                        />
                                    </div>
                                    <div className="double-input">
                                        <Input
                                            title="Select minimal hold duration"
                                            label="Hold Duration"
                                            value={holdDurationStartTime}
                                            onChange={(holdDurationStartTime) =>
                                                dispatch(
                                                    setHoldDurationStartTime(holdDurationStartTime),
                                                )
                                            }
                                            type={togglePlaceholder.holdDurationFrom}
                                            placeholder="From"
                                            aria-label="From: minimal hold duration"
                                            step={30}
                                            onFocus={() => {
                                                setTogglePlaceholder((obj) => ({
                                                    ...obj,
                                                    holdDurationFrom: 'time',
                                                }))
                                            }}
                                            style={{
                                                width: '124px',
                                            }}
                                            onBlur={() => {
                                                setTogglePlaceholder((obj) => ({
                                                    ...obj,
                                                    holdDurationFrom: holdDurationStartTime
                                                        ? 'time'
                                                        : 'text',
                                                }))
                                            }}
                                        />
                                        <Input
                                            title="Select maximum hold duration"
                                            label="‏‏‎ ‎"
                                            value={holdDurationEndTime}
                                            placeholder="To"
                                            aria-label="To: maximum hold duration"
                                            step={30}
                                            onChange={(holdDurationEndTime) =>
                                                dispatch(
                                                    setHoldDurationEndTime(holdDurationEndTime),
                                                )
                                            }
                                            type={togglePlaceholder.holdDurationTo}
                                            onFocus={() => {
                                                setTogglePlaceholder((obj) => ({
                                                    ...obj,
                                                    holdDurationTo: 'time',
                                                }))
                                            }}
                                            style={{
                                                width: '124px',
                                            }}
                                            onBlur={() => {
                                                setTogglePlaceholder((obj) => ({
                                                    ...obj,
                                                    holdDurationTo: holdDurationEndTime
                                                        ? 'time'
                                                        : 'text',
                                                }))
                                            }}
                                        />
                                    </div>

                                    {/* Cannot search liveVideoAssist and Barged at same time, so hide this control */}
                                    {(selectedChannelType !== ChannelType.VOICE || !liveVideoAssist) && (
                                        <Checkbox
                                            square
                                            checked={bargedOnly}
                                            label="Only show barged contacts"
                                            onChange={() => dispatch(setBargedOnly(!bargedOnly))}
                                            ariaLabel="Only show barged contacts"
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    <div className="row">
                        <Checkbox
                            title="Use advanced search"
                            square
                            onChange={(checked) => {
                                setShowAdvanced(checked)
                                dispatch(
                                    resetFormParams({
                                        ...formParams,
                                        contactID: '',
                                        externalID: '',
                                        phoneNumber: '',
                                        callDurationStartTime: '',
                                        callDurationEndTime: '',
                                        holdDurationStartTime: '',
                                        holdDurationEndTime: '',
                                        bargedOnly: false,
                                        initiationMethod: undefined,
                                    }),
                                )
                            }}
                            checked={showAdvanced}
                            label="Advanced Search"
                        />
                        <div className="contact-search-form-spacer" />
                        <div className=" contact-search-form-button" aria-busy={searching}>
                            <Button
                                type="reset"
                                title="Clear filters"
                                styling="white"
                                onClick={handleClearBothDates}
                            >
                                Clear
                            </Button>
                            <Button
                                type="submit"
                                title="Search contacts"
                                disabled={isSearchInvalid}
                                styling="primary"
                            >
                                {searching ? (
                                    <div data-testid="contact-search-loading">
                                        <ClipLoader color="#fff" size={20} />
                                    </div>
                                ) : (
                                    'Search'
                                )}
                            </Button>
                        </div>
                    </div>
                </Form>
            </Level>
        </Box>
    )
}

export default ContactSearch
